import { Download, Trash2 } from "lucide-react";
import { useState } from "react";
import { api } from "../../lib/api";
import { handleError } from "../../lib/errors";
import { notify } from "../../lib/notify";
import { Backup } from "../../types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";

interface BackupRowProps {
  backup: Backup;
  onDeleted: (id: string) => void;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

function statusVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "success":
      return "default";
    case "running":
    case "pending":
      return "secondary";
    case "failed":
      return "destructive";
    default:
      return "outline";
  }
}

export function BackupRow({ backup, onDeleted }: BackupRowProps) {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.backups.delete(backup.serviceId, backup.id);
      notify.success("Backup deleted");
      onDeleted(backup.id);
    } catch (err) {
      handleError(err, { scope: "BackupRow" });
    } finally {
      setDeleting(false);
    }
  };

  const handleDownload = () => {
    window.open(api.backups.downloadUrl(backup.serviceId, backup.id), "_blank");
  };

  return (
    <div className="flex items-center justify-between rounded-md bg-card p-3 text-sm">
      <div className="flex items-center gap-3 min-w-0">
        <Badge variant={statusVariant(backup.status)} className="shrink-0 capitalize">
          {backup.status}
        </Badge>
        <div className="min-w-0">
          <p className="font-medium capitalize">{backup.type} backup</p>
          <p className="text-xs text-muted-foreground">
            {formatDate(backup.createdAt)}
            {backup.sizeBytes > 0 && ` · ${formatBytes(backup.sizeBytes)}`}
            {" · "}
            <span className="capitalize">{backup.trigger}</span>
          </p>
          {backup.error && (
            <p className="text-xs text-destructive mt-0.5 truncate">{backup.error}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 ml-2 shrink-0">
        {backup.status === "success" && (
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleDownload} title="Download">
            <Download className="h-3.5 w-3.5" />
          </Button>
        )}
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-destructive hover:text-destructive"
              disabled={deleting}
              title="Delete"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete backup?</AlertDialogTitle>
              <AlertDialogDescription>
                This backup will be permanently deleted and cannot be restored.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
